/**
 * Agent Service
 * 
 * This service provides functions for managing agent names and roles
 * for different tasks and sections. It serves as a central point for
 * accessing agent information across the application.
 */

import { 
  getAgentForTask, 
  getDefaultAgentForSection, 
  getAllAgentsForSection,
  initializeTaskIdMap
} from '../utils/agentNames';

// API service for backend calls
import api from './api';

/**
 * Initialize the agent service by loading task mappings
 */
const initialize = async () => {
  try {
    // Load all tasks from the API to initialize the task ID mapping
    const response = await api.get('/api/tasks');
    if (response.data && Array.isArray(response.data)) {
      initializeTaskIdMap(response.data);
    }
  } catch (error) {
    console.error('Failed to initialize agent service:', error);
  }
};

/**
 * Get the agent for a specific task
 * @param {Object} task - The task object
 * @returns {Object} The agent information with name and role
 */
const getTaskAgent = (task) => {
  return getAgentForTask(task);
};

/**
 * Get the default agent for a section
 * @param {Object|string} section - The section object or ID
 * @returns {Object} The agent information with name and role
 */
const getSectionAgent = (section) => {
  return getDefaultAgentForSection(section);
};

/**
 * Get all agents for a section with their mapped tasks
 * @param {Object|string} section - The section object or ID
 * @returns {Object} Map of task keys to agent information
 */
const getSectionAgents = (section) => {
  return getAllAgentsForSection(section);
};

/**
 * Get the appropriate agent name and role based on the current context
 * @param {Object} context - The context object with task and/or section
 * @returns {Object} The agent information with name and role
 */
const getContextAgent = (context) => {
  // If we have a task, use task-specific agent
  if (context.task) {
    return getAgentForTask(context.task);
  }
  
  // If we have a section but no task, use section default agent
  if (context.section) {
    return getDefaultAgentForSection(context.section);
  }
  
  // If we have neither, use the global default
  return { name: 'Ailyce', role: 'AI Agent' };
};

/**
 * Get the appropriate avatar URL for an agent
 * @param {string} agentName - The agent's name
 * @returns {string} The URL to the agent's avatar image
 */
const getAgentAvatarUrl = (agentName) => {
  if (!agentName) return null;
  
  // For now, we'll return a placeholder. This could be expanded to map
  // agent names to specific avatar images from the assets folder
  return `/api/avatars/${agentName.toLowerCase()}`;
};

// Export the service
const agentService = {
  initialize,
  getTaskAgent,
  getSectionAgent,
  getSectionAgents,
  getContextAgent,
  getAgentAvatarUrl
};

export default agentService;